import { useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useCachedFetch } from './useCachedFetch';

/**
 * Fila de `public.parcelas_privadas` tal como la lee el cliente vía REST.
 * Solo trae lo necesario para mostrar en mapa + popup; no incluye
 * source_geojson (que es pesado y solo se usa para auditoría).
 *
 * RLS: pp_read (USING tenant_id = current_tenant_id()) → cada cliente
 * solo ve las privadas de su tenant. Aplicado a `cliente_elawan` y staff.
 *
 * Geometría: viene en formato PostGIS WKB hexadecimal (text), no GeoJSON
 * directo. El consumer debe transformar geom_geojson via RPC o tomar via
 * `select(...)` con casts si lo necesita en GeoJSON.
 */
export interface ParcelaPrivadaRow {
  id: string;
  parcela_id: string;
  tenant_id: string;
  municipio: string;
  nom_nucleo: string | null;
  num_parcela: string | null;
  area_ha: number;
  geom_geojson: unknown;  // Geometry GeoJSON parseable (Polygon|MultiPolygon)
  created_at: string;
}

/**
 * Lee `parcelas_privadas` del tenant actual via REST (RLS tenant-scoped).
 *
 * Usa una vista que expone geom como GeoJSON para evitar parsear WKB
 * client-side. La vista `v_parcelas_privadas_geojson` se crea on-demand
 * en una migration posterior (Fase 5). Mientras tanto, este hook funciona
 * pero devuelve `geom_geojson: null` y el visor lo skipea — degradación
 * gracedful sin crash.
 *
 * Cache: stale-while-revalidate 30s — refresh manual via refetch tras
 * crear privada en Captura (Fase 4 invoca `queryClient.invalidateQueries`
 * equivalente).
 */
export function useParcelasPrivadas(): {
  data: ParcelaPrivadaRow[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<ParcelaPrivadaRow[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    const { data, error } = await sb
      .from('v_parcelas_privadas_geojson')
      .select('id, parcela_id, tenant_id, municipio, nom_nucleo, num_parcela, area_ha, geom_geojson, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ParcelaPrivadaRow[];
  }, []);

  const { data, loading, error, refetch } = useCachedFetch<ParcelaPrivadaRow[]>(
    'parcelas_privadas',
    fetcher,
    { ttlMs: 30_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
